import { takeUntil } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { DrawingInspectionPointData } from 'app/viewmodels/idms/drawing-inspection-point-data';
import {I18NextPipe} from 'angular-i18next';
import { DrawingEquipmentData } from '../../../../viewmodels/idms/inspection-point-equipment-data';
import { DateTimeFormatterPipe } from '../../../pipes/date-time-formatter.pipe';
import {ThicknessReportTypePipe} from '../../../pipes/thickness-report-type.pipe';
import { DrawingPreviewInspectionPointService, InspectionPointHover } from './drawing-preview-inspection-point.service';
import { DrawingPreviewService } from './drawing-preview.service';

@Component({
  selector: 'hb-inspection-point-overlay',
  templateUrl: './inspection-point-overlay.component.html',
  styleUrls: ['./inspection-point-overlay.component.scss'],
})
export class InspectionPointOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('container', { static: true }) private container: ElementRef;

  public drawingEquipmentData: DrawingEquipmentData;
  public static readonly overlayPanelId = 'inspection-point-data';
  public readonly overlayWidthPixels: number = 240;

  private isLoadingData: boolean;
  private inspectionPointData: DrawingInspectionPointData;
  private inspectionPointHover: InspectionPointHover;
  private readonly isLoadingDescription = this.i18NextPipe.transform('Drawings.Loading');

  private unsubscribe: Subject<boolean> = new Subject();

  constructor(
    private drawingPreviewService: DrawingPreviewService,
    private inspectionPointService: DrawingPreviewInspectionPointService,
    private dateTimeFormatter: DateTimeFormatterPipe,
    private thicknessReportTypePipe: ThicknessReportTypePipe,
    private i18NextPipe: I18NextPipe
  ) {}

  ngOnInit() {
    this.drawingPreviewService.loadingDrawingEquipmentData.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.isLoadingData = true;
    });

    this.drawingPreviewService.drawingEquipmentData.pipe(takeUntil(this.unsubscribe)).subscribe(equipmentData => {
      this.drawingEquipmentData = equipmentData;
      this.setFocusedInspectionPointData();
      this.isLoadingData = false;
    });

    this.inspectionPointService.mouseOverInspectionPoint.pipe(takeUntil(this.unsubscribe)).subscribe((hover: InspectionPointHover) => {
      if (hover) {
        this.showOverlay(hover);
      } else {
        this.hideOverlay();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public getCriticality(): string {
    return this.drawingEquipmentData && this.drawingEquipmentData.criticality ? this.drawingEquipmentData.criticality : this.i18NextPipe.transform('Drawings.NotAvailable');
  }

  public getLastInspectionDate(): string {
    if (this.isLoadingData) {
      return this.isLoadingDescription;
    }

    return this.inspectionPointData && this.inspectionPointData.lastInspectionDate ? this.dateTimeFormatter.transform(this.inspectionPointData.lastInspectionDate.toString()) : this.i18NextPipe.transform('Drawings.NotAvailable');
  }

  public getThicknessReportTypeName(): string {
    if (this.isLoadingData) {
      return '';
    }

    if (this.inspectionPointData && this.inspectionPointData.lastThicknessReportType) {
      return `(${this.thicknessReportTypePipe.transform(this.inspectionPointData.lastThicknessReportType)})`;
    }

    return '';
  }

  public getDueDate(): string {
    if (this.isLoadingData) {
      return this.isLoadingDescription;
    }

    return this.inspectionPointData && this.inspectionPointData.dueDate ? this.dateTimeFormatter.transform(this.inspectionPointData.dueDate.toString()) : this.i18NextPipe.transform('Drawings.NotScheduled');
  }

  public calculateLeft(): number {
    if (!this.inspectionPointHover) {
      return 0;
    }

    const overlay = this.getOverlayElement();

    if (!overlay) {
      return 0;
    }

    const isPositionedRight = this.isPositionedRight(overlay);

    return isPositionedRight ? this.inspectionPointHover.clientX : this.inspectionPointHover.clientX - overlay.clientWidth;
  }

  public calculateMarginLeft(): number {
    if (!this.inspectionPointHover) {
      return 0;
    }

    const overlay = this.getOverlayElement();

    if (!overlay) {
      return 0;
    }

    return this.isPositionedRight(overlay) ? 10 : 0;
  }

  public calculateTop(): number {
    if (!this.inspectionPointHover) {
      return 0;
    }

    const overlay = this.getOverlayElement();

    if (!overlay) {
      return 0;
    }

    const isPositionedBelow = this.isPositionedBelow(overlay);

    return isPositionedBelow ? this.inspectionPointHover.clientY : this.inspectionPointHover.clientY - overlay.clientHeight;
  }

  public calculateMarginTop(): number {
    if (!this.inspectionPointHover) {
      return 0;
    }

    const overlay = this.getOverlayElement();

    if (!overlay) {
      return 0;
    }

    return this.isPositionedBelow(overlay) ? 10 : 0;
  }

  private isPositionedRight(overlay: HTMLElement): boolean {
    return (this.inspectionPointHover.svgElement.clientWidth - this.inspectionPointHover.clientX) > overlay.clientWidth;
  }

  private isPositionedBelow(overlay: HTMLElement): boolean {
    return (this.inspectionPointHover.svgElement.clientHeight - this.inspectionPointHover.clientY) > overlay.clientHeight;
  }

  private setFocusedInspectionPointData(): void {
    this.inspectionPointData = null;

    if (!this.inspectionPointHover || !this.drawingEquipmentData) {
      return;
    }

    if (this.inspectionPointHover.inspectionPoint.isAdHoc) {
      this.inspectionPointData = this.drawingEquipmentData.inspectionPointData.find(inspectionPointData => inspectionPointData.inspectionPointId === this.inspectionPointHover.inspectionPoint.inspectionPointId);
    } else {
      this.inspectionPointData = this.drawingEquipmentData.inspectionPointData.find(inspectionPointData => inspectionPointData.thicknessLocationId === this.inspectionPointHover.inspectionPoint.thicknessLocationId);
    }
  }

  private showOverlay(inspectionPointHover: InspectionPointHover): void {
    this.inspectionPointHover = inspectionPointHover;

    this.setFocusedInspectionPointData();

    const overlay = this.getOverlayElement();

    if (overlay) {
      overlay.style.display = 'block';
    }
  }

  private hideOverlay(): void {
    this.inspectionPointData = null;
    this.inspectionPointHover = null;
    const overlay = this.getOverlayElement();

    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  private getOverlayElement(): HTMLElement {
    return this.container && this.container.nativeElement ? this.container.nativeElement : null;
  }
}
